import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { google, web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";

import { AppThunk, RootState } from "@store";
import analytics, { track } from "@util/analytics";

import { createLoadableSelector, thunk } from "../utils";

import { selectActiveSubscription, updateOrders } from "./shopping";
import { fetchUser } from "./user";

const initialState = {
  selectedPlan: null as web.public_.SubscriptionPlan,
  activeSubscription: null as web.public_.IUserSubscription,
  userSubscriptions: null as web.public_.IUserSubscription[],
  subscriptionPlanInfoByPlan: null as Record<
    web.public_.SubscriptionPlan,
    web.public_.ISubscriptionPlanInfo
  >,
};

export type SubscriptionState = typeof initialState;

const subscriptionSlice = createSlice({
  name: "creditLine",
  initialState,
  reducers: {
    setSelectedPlan(
      state,
      { payload }: PayloadAction<SubscriptionState["selectedPlan"]>
    ) {
      state.selectedPlan = payload;
    },
    setActiveSubscription(
      state,
      { payload }: PayloadAction<SubscriptionState["activeSubscription"]>
    ) {
      state.activeSubscription = payload;
    },
    setUserSubscriptions(
      state,
      { payload }: PayloadAction<SubscriptionState["userSubscriptions"]>
    ) {
      state.userSubscriptions = payload;
    },
    setSubscriptionPlans(
      state,
      {
        payload,
      }: PayloadAction<SubscriptionState["subscriptionPlanInfoByPlan"]>
    ) {
      state.subscriptionPlanInfoByPlan = payload;
    },
  },
});

export const {
  setActiveSubscription,
  setUserSubscriptions,
  setSelectedPlan,
  setSubscriptionPlans,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;

export const selectSubscriptionPlanInfoByPlan = createLoadableSelector(
  () => (state: RootState) => state.subscription.subscriptionPlanInfoByPlan,
  {
    loadAction: () => fetchSubscriptionPlans(),
  }
);

export const selectUserSubscriptions = () => (state: RootState) =>
  state.subscription.userSubscriptions;

export const openSubscriptionAndCreditLine = (
  autopayEnabled: boolean,
  subscriptionPlanTitle: web.public_.SubscriptionPlan,
  autopay: web.public_.AutoPay.ISettings,
  productToken: string,
  firstPaymentDate: null | google.protobuf.ITimestamp
): AppThunk<Promise<web.public_.OpenSubscriptionAndCreditLineResponse>> => (
  dispatch
) => {
  return webRPC.Subscription.openSubscriptionAndCreditLine({
    autorenew: autopayEnabled,
    subscriptionPlanTitle,
    autoPay: autopay,
    productToken,
    firstPaymentDate,
  }).then(
    handleProtoStatus({
      async SUCCESS(data) {
        await dispatch(setSelectedPlan(data.userSubscription.plan));
        await dispatch(setActiveSubscription(data.userSubscription));
        await dispatch(fetchUser.creditLine());
        analytics.convert("Credit Account Open");
        track(
          "CA - Subscription - Refactor - Open Subscription and Credit Line Success"
        );
      },
      _DEFAULT: handleFailedStatus("Failed to checkout"),
    })
  );
};

export const openSubscription = (args: web.public_.IOpenSubscriptionRequest) =>
  thunk((dispatch) =>
    webRPC.Subscription.openSubscription(args).then(
      handleProtoStatus({
        SUCCESS(data) {
          return data.userSubscription;
        },
        _DEFAULT: handleFailedStatus("Failed to open subscription"),
      })
    )
  );

export const renewSubscriptionAndCreditLine = (
  subscriptionPlanTitle: web.public_.SubscriptionPlan,
  productToken: string
): AppThunk<Promise<web.public_.RenewSubscriptionAndCreditLineResponse>> => (
  dispatch
) => {
  return webRPC.Subscription.renewSubscriptionAndCreditLine({
    subscriptionPlanTitle,
    productToken,
  }).then(
    handleProtoStatus({
      async SUCCESS(data) {
        await dispatch(setSelectedPlan(data.userSubscription.plan));
        await dispatch(setActiveSubscription(data.userSubscription));
        await dispatch(fetchUser.creditLine());
        analytics.convert("Credit Account Open");
      },
      _DEFAULT: handleFailedStatus("Failed to renew tradeline"),
    })
  );
};

export const fetchSubscription = (): AppThunk<
  Promise<web.public_.UserSubscription>
> => async (dispatch) =>
  webRPC.Subscription.getUserSubscription({}).then(
    handleProtoStatus({
      SUCCESS({ userSubscription }) {
        if (userSubscription) {
          dispatch(setActiveSubscription(userSubscription));
        }

        return userSubscription;
      },
      _DEFAULT: handleFailedStatus("Failed to get user subscription info"),
    })
  );

export const fetchAllSubscriptions = (): AppThunk<
  Promise<web.public_.UserSubscription[]>
> => async (dispatch) =>
  webRPC.Subscription.getAllUserSubscriptions({}).then(
    handleProtoStatus({
      SUCCESS({ userSubscriptions }) {
        dispatch(setUserSubscriptions(userSubscriptions));
        return userSubscriptions;
      },
      _DEFAULT: handleFailedStatus("Failed to get user subscription info"),
    })
  );

export const fetchSubscriptionPlans = () =>
  thunk((dispatch) =>
    webRPC.Subscription.listSubscriptionPlans({}).then(
      handleProtoStatus({
        SUCCESS(data) {
          const subscriptionPlans = data.subscriptionPlans.filter(
            (plan) => plan.noCreditLine
          );
          const subscriptionPlanInfoByPlan = Object.fromEntries(
            subscriptionPlans.map((plan) => [plan.subscriptionPlan, plan])
          );

          dispatch(setSubscriptionPlans(subscriptionPlanInfoByPlan));

          return subscriptionPlans;
        },
        _DEFAULT: handleFailedStatus("Failed to load subscription plans."),
      })
    )
  );

export const updateAutoRenewalStatus = (
  status: keyof typeof web.public_.AutoRenew.Status
) =>
  thunk((dispatch, getState) =>
    webRPC.Subscription.updateAutoRenewalStatus({
      userSubscription: {
        autoRenew: {
          status: web.public_.AutoRenew.Status[status],
        },
        token: selectActiveSubscription()(getState()).token,
      },
    }).then(
      handleProtoStatus({
        SUCCESS() {
          dispatch(updateOrders());
          track("CA - Subscription - Update Auto-Renewal Success");
        },
        _DEFAULT: handleFailedStatus("Failed to update subscription status"),
      })
    )
  );

export type Plan = "basic" | "premium" | "ultimate";

const { SubscriptionPlan } = web.public_;

export namespace Plan {
  export const plans = ["basic", "premium", "ultimate"] as const;

  export const toProto = {
    basic: SubscriptionPlan.BASIC,
    premium: SubscriptionPlan.PREMIUM,
    ultimate: SubscriptionPlan.ULTIMATE,
  };
  export const byProto: Partial<Record<web.public_.SubscriptionPlan, Plan>> = {
    [SubscriptionPlan.BASIC]: "basic",
    [SubscriptionPlan.PREMIUM]: "premium",
    [SubscriptionPlan.ULTIMATE]: "ultimate",
  };
  const nameByPlan = {
    basic: "Basic",
    premium: "Premium",
    ultimate: "Ultimate",
  };
  export const name = Object.assign(
    (plan: Plan | web.public_.SubscriptionPlan) =>
      nameByPlan[byProto[plan] ?? plan],
    {
      full: (plan: Plan | web.public_.SubscriptionPlan) =>
        `Kikoff Credit Service ${name(plan)}`,
    }
  );

  export const hierarchy: Record<Plan, number> = {
    basic: 0,
    premium: 1,
    ultimate: 2,
  };
}
